import { useCallback, useEffect, useRef, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import Modal from './Modal'
import { useCreateDevice } from '../../hooks/useDevices'
import { iotDevicesApi } from '../../services/iotDevicesApi'
import { useTemplates } from '../../hooks/useTemplates'

/** Valores como a API .NET espera (JsonStringEnumConverter + nome do enum). */
const KIND_OPTIONS = [
  { value: 'LoRaWan', label: 'LoRaWAN' },
  { value: 'Mqtt', label: 'MQTT' },
  { value: 'CustomApi', label: 'Custom API' },
]

export default function DeviceModal({ isOpen, onClose, applications = [] }) {
  const { t } = useTranslation()
  const createDevice = useCreateDevice()

  const [formData, setFormData] = useState({
    applicationId: '',
    name: '',
    devEui: '',
    description: '',
    kind: 'LoRaWan',
    templateId: '',
  })

  const { data: templates } = useTemplates(formData.applicationId || undefined)
  const [errors, setErrors] = useState({})
  const [checkingDevEui, setCheckingDevEui] = useState(false)
  const devEuiCheckTimer = useRef(null)

  useEffect(() => {
    if (!isOpen) return
    const first = applications[0]?.id
    setFormData((prev) => ({
      ...prev,
      applicationId: prev.applicationId || (first ? String(first) : ''),
    }))
  }, [isOpen, applications])

  const checkDevEuiRemote = useCallback(async (raw, kind) => {
    const devEui = kind === 'LoRaWan'
      ? raw.replace(/[-\s:]/g, '').toUpperCase()
      : raw
    if (devEui.length < 4) return
    try {
      setCheckingDevEui(true)
      const { exists } = await iotDevicesApi.checkDevEui(devEui)
      if (exists) {
        setErrors((prev) => ({ ...prev, devEui: t('devices.modalCreate.devEuiDuplicate') }))
      }
    } catch {
      // silently ignore check errors
    } finally {
      setCheckingDevEui(false)
    }
  }, [t])

  const handleChange = (e) => {
    const { name, value } = e.target
    const patch = { [name]: value }
    if (name === 'applicationId') patch.templateId = ''
    setFormData((prev) => ({ ...prev, ...patch }))
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: null }))

    if (name === 'devEui') {
      clearTimeout(devEuiCheckTimer.current)
      const trimmed = value.trim()
      if (trimmed.length >= 4) {
        devEuiCheckTimer.current = setTimeout(
          () => checkDevEuiRemote(trimmed, formData.kind),
          600,
        )
      }
    }
  }

  const validate = () => {
    const newErrors = {}
    if (!formData.applicationId?.trim()) {
      newErrors.applicationId = t('devices.modalCreate.applicationRequired')
    }
    if (!formData.name.trim()) {
      newErrors.name = t('devices.modalCreate.nameRequired')
    }
    const raw = formData.devEui.trim()
    if (!raw) {
      newErrors.devEui = t('devices.modalCreate.devEuiRequired')
    } else if (raw.length > 128) {
      newErrors.devEui = t('devices.modalCreate.devEuiMax')
    } else if (formData.kind === 'LoRaWan') {
      const eui = raw.replace(/[-\s:]/g, '')
      if (!/^[0-9A-Fa-f]+$/.test(eui)) {
        newErrors.devEui = t('devices.modalCreate.devEuiLorawanHex')
      } else if (eui.length !== 16) {
        newErrors.devEui = t('devices.modalCreate.devEuiLorawanLen')
      }
    } else if (!/^[\w.\-:]+$/i.test(raw)) {
      newErrors.devEui = t('devices.modalCreate.devEuiGeneric')
    }
    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    if (!validate()) return

    const rawDev = formData.devEui.trim()
    const devEui =
      formData.kind === 'LoRaWan'
        ? rawDev.replace(/[-\s:]/g, '').toUpperCase()
        : rawDev
    const payload = {
      applicationId: formData.applicationId,
      name: formData.name.trim(),
      devEui,
      description: formData.description.trim() || null,
      kind: formData.kind,
      templateId: formData.templateId || null,
      vpnEnabled: false,
    }

    try {
      await createDevice.mutateAsync(payload)
      onClose()
      setFormData({
        applicationId: applications[0]?.id ? String(applications[0].id) : '',
        name: '',
        devEui: '',
        description: '',
        kind: 'LoRaWan',
        templateId: '',
      })
      setErrors({})
    } catch (error) {
      const msg = error?.response?.data?.message || error.message || t('devices.modalCreate.createFailed')
      if (msg.toLowerCase().includes('deveui')) {
        setErrors((prev) => ({ ...prev, devEui: msg }))
      } else {
        alert(msg)
      }
    }
  }

  const noApps = !applications?.length

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={t('devices.modalCreate.title')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        {noApps ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-md p-3">
            <Trans i18nKey="devices.modalCreate.noAppsHint" components={{ strong: <strong /> }} />
          </p>
        ) : null}

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('devices.modalCreate.applicationLabel')} <span className="text-red-500">*</span>
          </label>
          <select
            name="applicationId"
            value={formData.applicationId}
            onChange={handleChange}
            disabled={noApps}
            className={`input w-full ${errors.applicationId ? 'border-red-500' : ''}`}
          >
            <option value="">{t('devices.modalCreate.selectPlaceholder')}</option>
            {applications.map((app) => (
              <option key={app.id} value={app.id}>
                {app.name}
              </option>
            ))}
          </select>
          {errors.applicationId && (
            <p className="mt-1 text-sm text-red-600">{errors.applicationId}</p>
          )}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('devices.modalCreate.nameLabel')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            name="name"
            value={formData.name}
            onChange={handleChange}
            className={`input w-full ${errors.name ? 'border-red-500' : ''}`}
            placeholder={t('devices.modalCreate.namePlaceholder')}
          />
          {errors.name && <p className="mt-1 text-sm text-red-600">{errors.name}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">
            {t('devices.modalCreate.devEuiLabel')} <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <input
              type="text"
              name="devEui"
              value={formData.devEui}
              onChange={handleChange}
              onBlur={() => {
                const raw = formData.devEui.trim()
                if (raw.length >= 4 && !errors.devEui) {
                  checkDevEuiRemote(raw, formData.kind)
                }
              }}
              className={`input w-full font-mono ${errors.devEui ? 'border-red-500' : ''}`}
              placeholder={t('devices.modalCreate.devEuiPlaceholder')}
              autoComplete="off"
            />
            {checkingDevEui && (
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-xs text-gray-400">
                {t('devices.modalCreate.devEuiVerifying')}
              </span>
            )}
          </div>
          {errors.devEui && <p className="mt-1 text-sm text-red-600">{errors.devEui}</p>}
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('devices.modalCreate.kindLabel')}</label>
          <select
            name="kind"
            value={formData.kind}
            onChange={handleChange}
            className="input w-full"
          >
            {KIND_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('devices.modalCreate.templateLabel')}</label>
          <select
            name="templateId"
            value={formData.templateId}
            onChange={handleChange}
            disabled={!formData.applicationId}
            className="input w-full"
          >
            <option value="">{t('devices.modalCreate.templateNone')}</option>
            {(templates ?? []).map((tpl) => (
              <option key={tpl.id} value={tpl.id}>
                {tpl.name}
              </option>
            ))}
          </select>
          <p className="mt-1 text-xs text-gray-500">
            {t('devices.modalCreate.templateHelp')}
          </p>
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">{t('devices.modalCreate.descriptionLabel')}</label>
          <textarea
            name="description"
            value={formData.description}
            onChange={handleChange}
            rows={2}
            className="input w-full"
            placeholder={t('devices.modalCreate.descriptionPlaceholder')}
          />
        </div>

        <div className="flex justify-end gap-2 pt-2">
          <button type="button" className="btn btn-outline" onClick={onClose}>
            {t('common.buttons.cancel')}
          </button>
          <button
            type="submit"
            className="btn btn-primary"
            disabled={noApps || createDevice.isPending}
          >
            {createDevice.isPending ? t('devices.modalCreate.submitLoading') : t('devices.modalCreate.submit')}
          </button>
        </div>
      </form>
    </Modal>
  )
}
